import { useMemo } from 'react';
import { theme } from '@frontend/theme';
import { Text, Truncated, useModalControl } from '@frontend/design-system';
import { OutOfOfficeEventCardModal } from '../../../modals/out-of-office-event-card-modal';
import { EventCardComponent } from '../../../schedule-calendar-components/event-card';
import { getDurationFromHours } from '../../utils';
import { GridEventCard } from '../atoms/grid-event-card';

type GridExceptionCardProps = {
  id: string;
  title: string;
  startDate: string;
  startHour: string;
  endHour: string;
  calendarDate?: string;
  locationName?: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  eventTypeColor?: string;
  backgroundColor?: string;
  /**
   * Internal event type - maps to OutOfOfficeEventCardModal's expected types.
   * Note: For EventCardComponent, 'exception' maps to 'outOfOffice', 'break' maps to 'break'
   */
  eventType?: 'exception' | 'break';
};

//#region Card

export const GridExceptionCard = ({
  id,
  title,
  startDate,
  startHour,
  endHour,
  calendarDate,
  locationName = '',
  locationId,
  entityId,
  entityName,
  eventTypeColor,
  backgroundColor = theme.colors.neutral20,
  eventType = 'exception',
}: GridExceptionCardProps) => {
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);
  const primaryColor = eventTypeColor ?? theme.colors.neutral50;

  const { triggerProps: outOfOfficeTriggerProps, modalProps: outOfOfficeModalProps } = useModalControl();

  // Map internal eventType to EventCardComponent's and OutOfOfficeEventCardModal's expected EventType
  const eventCardType = eventType === 'exception' ? 'outOfOffice' : 'break';

  return (
    <GridEventCard id={id} primaryColor={primaryColor} backgroundColor={backgroundColor}>
      <GridEventCard.ShellWrapper onClick={outOfOfficeTriggerProps.onClick} css={{ cursor: 'pointer', height: '100%' }}>
        <GridEventCard.Shell>
          <GridEventCard.EventColoredLine />
          <EventCardComponent
            id={id}
            eventType={eventCardType}
            title={title}
            duration={duration}
            locationName={locationName}
            locationId={locationId}
            providerId={entityId}
            providerName={entityName}
            isExpanded={false}
            startDate={startDate}
          />
        </GridEventCard.Shell>
      </GridEventCard.ShellWrapper>
      <OutOfOfficeEventCardModal
        id={id}
        eventType={eventCardType}
        title={title}
        duration={duration}
        locationId={locationId}
        locationName={locationName}
        providerId={entityId}
        startDate={startDate}
        providerName={entityName}
        calendarDate={calendarDate}
        modalProps={outOfOfficeModalProps}
      />
    </GridEventCard>
  );
};

//#endregion

//#region Tooltip

type GridExceptionTooltipProps = {
  title: string;
  startHour: string;
  endHour: string;
};

export const GridExceptionTooltip = ({ title, startHour, endHour }: GridExceptionTooltipProps) => {
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);

  return (
    <div css={exceptionTooltipStyle}>
      <Truncated as={Text} weight='bold' size='medium' color='white'>
        {title}
      </Truncated>
      <Truncated as={Text} size='small' color='white'>
        {duration}
      </Truncated>
    </div>
  );
};

const exceptionTooltipStyle = {
  display: 'grid',
  gap: theme.spacing(0.25),
  maxWidth: '200px',
};

//#endregion
